import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Ticket, AdminUser, Machine, Customer } from '../types';
import { ticketApi } from '../api/tickets';
import { adminApi } from '../api/admin';
import { machineApi, customerApi } from '../api/machines';
import { useAuth } from '../context/AuthContext';
import { ticketStatusMeta } from '../utils/ticketStatus';
import { Toast, useToast } from '../components/Toast';
import { PriorityBadge } from '../components/TicketsTab';

const AssignmentsPage: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [staff, setStaff] = useState<AdminUser[]>([]);
  const [machines, setMachines] = useState<Map<string, Machine>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('unassigned');
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedNip, setSelectedNip] = useState('');
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tRes, uRes, mRes, cRes] = await Promise.all([
        ticketApi.listAll(),
        adminApi.listUsers(),
        machineApi.getAll(),
        customerApi.getAll(true),
      ]);
      setTickets(tRes.data);
      setStaff(uRes.data.filter((u) =>
        u.roles.includes('ADMIN') || u.roles.includes('EMPLOYEE_ADMIN') || u.roles.includes('EMPLOYEE')));
      const map = new Map<string, Machine>();
      mRes.data.forEach((m) => map.set(m.id, m));
      setMachines(map);
      setCustomers(cRes.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user?.roles?.includes('ADMIN') && !user?.roles?.includes('EMPLOYEE_ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const onAssign = async (ticketId: string, userId: string | null) => {
    try {
      await ticketApi.assign(ticketId, userId);
      await load();
      showToast('success', userId ? 'Zgłoszenie przypisane' : 'Przypisanie usunięte');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się przypisać zgłoszenia');
    }
  };

  const nipToName: Record<string, string> = {};
  customers.forEach((c) => { if (c.nip) nipToName[c.nip] = c.companyName || ''; });

  const customerOptions = (() => {
    const nips = Array.from(new Set(
      tickets.map((t) => machines.get(t.machineId)?.ownerNip).filter(Boolean) as string[]
    ));
    return nips
      .map((nip) => ({ nip, label: nipToName[nip] ? `${nipToName[nip]} (NIP ${nip})` : `NIP ${nip}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  })();

  const filtered = tickets.filter((t) => {
    if (filter === 'unassigned' && t.assignedToUserId) return false;
    if (filter === 'assigned' && !t.assignedToUserId) return false;
    if (selectedNip && machines.get(t.machineId)?.ownerNip !== selectedNip) return false;
    return true;
  });

  return (
    <div>
      <Toast toast={toast} onDismiss={dismissToast} />
      <div className="page-header">
        <h1 className="page-title">Przypisywanie zgłoszeń</h1>
      </div>

      <div className="filters-bar" style={{ marginBottom: '1rem' }}>
        <div className="filter-group">
          <label className="form-label">Pokaż</label>
          <select
            className="form-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'unassigned' | 'assigned')}
          >
            <option value="unassigned">Nieprzypisane</option>
            <option value="assigned">Przypisane</option>
            <option value="all">Wszystkie</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Klient</label>
          <select
            className="form-select"
            value={selectedNip}
            onChange={(e) => setSelectedNip(e.target.value)}
            style={{ minWidth: 240 }}
          >
            <option value="">Wszyscy klienci</option>
            {customerOptions.map((c) => (
              <option key={c.nip} value={c.nip}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">Brak zgłoszeń pasujących do filtru</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
            fontSize: '0.875rem',
          }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-alt)', textAlign: 'left' }}>
                <th style={{ padding: '0.625rem 0.75rem' }}>Zgłoszenie</th>
                <th style={{ padding: '0.625rem 0.75rem' }}>Maszyna</th>
                <th style={{ padding: '0.625rem 0.75rem' }}>Status</th>
                <th style={{ padding: '0.625rem 0.75rem' }}>Priorytet</th>
                <th style={{ padding: '0.625rem 0.75rem' }}>Klient</th>
                <th style={{ padding: '0.625rem 0.75rem' }}>Przypisz do</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const m = machines.get(t.machineId);
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                        #{t.ticketNumber}
                      </div>
                      <div>
                        {m ? (
                          <Link to={`/machines/${t.machineId}?tab=tickets`} style={{ fontWeight: 600 }}>
                            {t.title}
                          </Link>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{t.title}</span>
                        )}
                      </div>
                      <div className="item-meta" style={{ fontSize: '0.7rem' }}>
                        {new Date(t.createdAt).toLocaleString('pl-PL')}
                      </div>
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      {m ? m.name : <em>—</em>}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <span className={ticketStatusMeta(t.status).badgeClass}>{ticketStatusMeta(t.status).label}</span>
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <PriorityBadge priority={t.priority} />
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      {m ? (nipToName[m.ownerNip] || m.ownerNip) : '—'}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem', minWidth: 200 }}>
                      <select
                        className="form-select"
                        value={t.assignedToUserId || ''}
                        onChange={(e) => onAssign(t.id, e.target.value || null)}
                        style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.8125rem' }}
                      >
                        <option value="">— nikt —</option>
                        {staff.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.firstName} {s.lastName} ({s.roles[0] === 'ADMIN' ? 'Admin' : s.roles[0] === 'EMPLOYEE_ADMIN' ? 'Admin integratora' : 'Pracownik'})
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssignmentsPage;
